/**
 * Service for handling search operations in PDF and EPUB documents
 */

/**
 * Normalize whitespace in text while preserving word boundaries
 * PDF.js recommended approach for text processing
 */
const normalizeWhitespace = (text) => {
  return text.replace(/\s+/g, " ").trim();
};

const clampNumber = (value, min, max) =>
  Math.min(max, Math.max(min, Number(value) || min));

const getChapterPageBounds = (chapter) => {
  const startPage = Number(chapter?.start_page) || 1;
  const pageCount = Math.max(1, Number(chapter?.page_count) || 1);
  const endPage =
    Number(chapter?.end_page) || startPage + Math.max(0, pageCount - 1);

  return {
    startPage,
    endPage: Math.max(startPage, endPage),
    pageCount: Math.max(1, endPage - startPage + 1),
  };
};

const getSyntheticPageForChapterOffset = (chapter, charOffset, totalChars) => {
  const { startPage, endPage, pageCount } = getChapterPageBounds(chapter);
  if (pageCount <= 1) return startPage;

  const denominator =
    Number(totalChars) > 0
      ? Number(totalChars)
      : Math.max(1, Number(chapter?.character_count) || 1);
  const progress = clampNumber(Number(charOffset) / denominator, 0, 0.999999);
  const pageOffset = Math.floor(progress * pageCount);
  return clampNumber(startPage + pageOffset, startPage, endPage);
};

/**
 * Create a stable search result id for deduplication.
 * We intentionally key PDF snippets by page + normalized query + normalized
 * context so repeated extraction passes (or hidden duplicate text layers)
 * don't render identical snippets multiple times.
 */
const createSearchResultId = ({ pageNumber, query, context }) => {
  const normalizedQuery = normalizeWhitespace(
    String(query || ""),
  ).toLowerCase();
  const normalizedContext = normalizeWhitespace(
    String(context || ""),
  ).toLowerCase();
  return `pdf:${Number(pageNumber) || 0}:${normalizedQuery}:${normalizedContext}`;
};

/**
 * Build text content string from PDF.js text items
 * Uses PDF.js recommended approach respecting text item boundaries
 */
const buildTextContentString = (textContent) => {
  const items = textContent.items;
  const strBuf = [];

  for (let i = 0; i < items.length; i++) {
    const item = items[i];
    if (item.str) {
      strBuf.push(item.str);
    }

    // Add space if this item doesn't end with space and next item exists
    if (item.hasEOL || (i < items.length - 1 && !item.str.endsWith(" "))) {
      strBuf.push(" ");
    }
  }

  return normalizeWhitespace(strBuf.join(""));
};

/**
 * Extract context around a match using sentence boundaries
 * PDF.js recommended approach for context extraction
 */
const extractContext = (text, matchStart, matchEnd, contextChars = 500) => {
  const beforeText = text.substring(
    Math.max(0, matchStart - contextChars),
    matchStart,
  );
  const matchText = text.substring(matchStart, matchEnd);
  const afterText = text.substring(
    matchEnd,
    Math.min(text.length, matchEnd + contextChars),
  );

  // Find sentence boundaries (. ! ?) followed by space or end
  const sentenceEnd = /[.!?](?:\s|$)/g;

  let contextStart = 0;
  let lastMatch = -1;
  sentenceEnd.lastIndex = 0;

  while (sentenceEnd.exec(beforeText)) {
    lastMatch = sentenceEnd.lastIndex;
  }

  if (lastMatch > 0) {
    contextStart = lastMatch;
  }

  let contextEnd = afterText.length;
  sentenceEnd.lastIndex = 0;
  const afterMatch = sentenceEnd.exec(afterText);

  if (afterMatch) {
    contextEnd = afterMatch.index + afterMatch[0].length;
  }

  const before = beforeText.substring(contextStart);
  const after = afterText.substring(0, contextEnd);

  let context = before + matchText + after;
  context = normalizeWhitespace(context);

  // Add ellipsis if text was truncated
  if (matchStart > contextChars && contextStart > 0) {
    context = "... " + context;
  }
  if (matchEnd + contextChars < text.length && contextEnd < afterText.length) {
    context = context + " ...";
  }

  return context;
};

/**
 * Search for text in a PDF document using PDF.js recommended approach
 * @param {Object} pdfDoc - PDF.js document instance
 * @param {string} query - Search query
 * @param {number} totalPages - Total number of pages
 * @returns {Promise<Array>} Array of search results with page numbers and context
 */
export const searchPdf = async (pdfDoc, query, totalPages, options = {}) => {
  if (!query || !query.trim()) {
    return [];
  }

  const { isChunked = false, loadedChunks = {}, chunkSize = 5 } = options;

  // Normalize query whitespace to match the way page text is built.  Users
  // can select across lines or copy/paste content that contains newlines;
  // collapsing to single spaces ensures the index search works.
  const searchQuery = normalizeWhitespace(query.trim());
  const searchQueryLower = searchQuery.toLowerCase();
  const results = [];
  const maxResultsPerPage = 50;

  try {
    // If document is chunked, only iterate through loaded chunks to avoid
    // requesting pages that aren't available (which causes PDF.js errors).
    if (isChunked) {
      const chunkIndices = Object.keys(loadedChunks)
        .map((k) => parseInt(k, 10))
        .filter((k) => !Number.isNaN(k))
        .sort((a, b) => a - b);

      for (const chunkIndex of chunkIndices) {
        const chunkDoc = loadedChunks[chunkIndex];
        if (!chunkDoc) continue;

        const startGlobalPage = chunkIndex * chunkSize + 1;
        const numPagesInChunk = chunkDoc.numPages || 0;

        for (let localPage = 1; localPage <= numPagesInChunk; localPage++) {
          const pageNum = startGlobalPage + (localPage - 1);
          try {
            const page = await chunkDoc.getPage(localPage);
            const textContent = await page.getTextContent();

            // Build text string using PDF.js recommended method
            const pageText = buildTextContentString(textContent);
            const pageTextLower = pageText.toLowerCase();

            // Use PDF.js pattern: find all matches in normalized text
            let matchIndex = 0;
            let occurrenceCount = 0;

            while (matchIndex !== -1 && occurrenceCount < maxResultsPerPage) {
              matchIndex = pageTextLower.indexOf(searchQueryLower, matchIndex);

              if (matchIndex === -1) {
                break;
              }

              // Extract context using PDF.js recommended approach
              const context = extractContext(
                pageText,
                matchIndex,
                matchIndex + searchQuery.length,
              );

              results.push({
                pageNumber: pageNum,
                matchIndex: matchIndex,
                context: context,
                query: searchQuery,
                occurrenceId: `pdf:${pageNum}:${searchQueryLower}:${matchIndex}`,
                occurrenceCount: 1,
                resultId: createSearchResultId({
                  pageNumber: pageNum,
                  query: searchQuery,
                  context,
                }),
              });

              // Move to next potential match
              matchIndex += searchQuery.length;
              occurrenceCount++;
            }
          } catch (error) {
            console.error(`Error searching page ${pageNum}:`, error);
            // Continue to next page on error
          }
        }
      }
    } else {
      // Non-chunked: search through all pages using PDF.js recommended approach
      for (let pageNum = 1; pageNum <= totalPages; pageNum++) {
        try {
          const page = await pdfDoc.getPage(pageNum);
          const textContent = await page.getTextContent();

          // Build text string using PDF.js recommended method
          const pageText = buildTextContentString(textContent);
          const pageTextLower = pageText.toLowerCase();

          // Use PDF.js pattern: find all matches in normalized text
          let matchIndex = 0;
          let occurrenceCount = 0;

          while (matchIndex !== -1 && occurrenceCount < maxResultsPerPage) {
            matchIndex = pageTextLower.indexOf(searchQueryLower, matchIndex);

            if (matchIndex === -1) {
              break;
            }

            // Extract context using PDF.js recommended approach
            const context = extractContext(
              pageText,
              matchIndex,
              matchIndex + searchQuery.length,
            );

            results.push({
              pageNumber: pageNum,
              matchIndex: matchIndex,
              context: context,
              query: searchQuery,
              occurrenceId: `pdf:${pageNum}:${searchQueryLower}:${matchIndex}`,
              occurrenceCount: 1,
              resultId: createSearchResultId({
                pageNumber: pageNum,
                query: searchQuery,
                context,
              }),
            });

            // Move to next potential match
            matchIndex += searchQuery.length;
            occurrenceCount++;
          }
        } catch (error) {
          console.error(`Error searching page ${pageNum}:`, error);
          // Continue to next page on error
        }
      }
    }
  } catch (error) {
    console.error("Error in PDF search:", error);
  }

  return results;
};

/**
 * Search for text in an EPUB document
 * @param {Object} epubBook - epubjs Book instance
 * @param {Object} rendition - epubjs Rendition instance
 * @param {string} query - Search query
 * @param {Object} options - { isChunked, loadedChunks, metadata }
 * @returns {Promise<Array>} Array of search results with section indices and CFI ranges
 */
export const searchEpub = async (epubBook, rendition, query, options = {}) => {
  if (!epubBook || !query || !query.trim()) {
    return [];
  }

  const { isChunked = false, loadedChunks = {}, metadata = null } = options;

  const results = [];
  // rawQuery preserves the original string for later punctuation-based
  // splitting.  searchQuery is the normalised form used for the first pass.
  const rawQuery = query.trim();

  // collapse whitespace, convert NBSPs to spaces, and strip basic
  // punctuation – epub.js.search is literal and sensitive to these details.
  let searchQuery = normalizeWhitespace(rawQuery)
    .replace(/\u00A0/g, " ")
    .replace(/[.,;:!?]/g, "");
  const load =
    epubBook.load?.bind(epubBook) || epubBook.request?.bind(epubBook);

  if (!load) {
    console.warn("EPUB search: No load method available");
    return [];
  }

  const searchSingleBook = async (
    book,
    chapterStartPage = 1,
    chapterMetadata = null,
  ) => {
    const spine = book?.spine;
    if (!spine || spine.length === 0) return [];

    const bookLoad = book.load?.bind(book) || book.request?.bind(book);
    if (!bookLoad) return [];

    const bookResults = [];
    let cumulativeChars = 0;
    const searchQueryLower = searchQuery.toLowerCase();
    for (let i = 0; i < spine.length; i++) {
      try {
        const section = spine.get(i);
        if (!section) continue;

        await section.load(bookLoad);
        const sectionText =
          section.document?.body?.innerText ||
          section.document?.body?.textContent ||
          "";
        const sectionTextLength = sectionText.length;
        const sectionTextLower = sectionText.toLowerCase();
        const sectionStartChar = cumulativeChars;
        let searchFrom = 0;

        const rawMatches =
          section.search?.(searchQuery) ?? section.find?.(searchQuery) ?? [];
        const maxPerSection = 50;
        const matches = rawMatches.slice(0, maxPerSection);

        if (matches.length > 0) {
          // Assign unique matchIndex to each match within the section
          matches.forEach((match, idx) => {
            let localOffset = sectionTextLower.indexOf(
              searchQueryLower,
              searchFrom,
            );
            if (localOffset >= 0) {
              searchFrom = localOffset + Math.max(1, searchQueryLower.length);
            } else {
              localOffset = Math.floor(
                (sectionTextLength * (idx + 0.5)) / matches.length,
              );
            }

            const pageNumber = chapterMetadata
              ? getSyntheticPageForChapterOffset(
                  chapterMetadata,
                  sectionStartChar + localOffset,
                  Number(chapterMetadata.character_count) || null,
                )
              : chapterStartPage + i;

            bookResults.push({
              sectionIndex: i,
              pageNumber,
              matchIndex: idx, // Now unique within section: 0, 1, 2, ...
              context: match.excerpt || searchQuery,
              query: searchQuery,
              cfi: match.cfi || null,
              chapter_number: undefined, // Will be set by caller if in chunked mode
            });
          });
        }

        section.unload?.();
        cumulativeChars += sectionTextLength;
      } catch (error) {
        console.error(`Error searching EPUB section ${i}:`, error);
      }
    }
    return bookResults;
  };

  try {
    if (
      isChunked &&
      metadata?.chapters?.length > 0 &&
      Object.keys(loadedChunks).length > 0
    ) {
      for (const chapter of metadata.chapters) {
        const chapterNum = Number(chapter.chapter_number);
        const chunkBook = loadedChunks[chapterNum];
        if (!chunkBook) continue;

        const startPage = Number(chapter.start_page) || 1;
        const chunkResults = await searchSingleBook(
          chunkBook,
          startPage,
          chapter,
        );

        // Add chapter_number to results for chunked mode
        chunkResults.forEach((result) => {
          result.chapter_number = chapterNum;
        });

        results.push(...chunkResults);
      }
    } else {
      const spine = epubBook.spine;
      if (!spine || spine.length === 0) return [];

      for (let i = 0; i < spine.length; i++) {
        try {
          const section = spine.get(i);
          if (!section) continue;

          await section.load(load);

          const rawMatches =
            section.search?.(searchQuery) ?? section.find?.(searchQuery) ?? [];
          const maxPerSection = 50;
          const matches = rawMatches.slice(0, maxPerSection);

          if (matches.length > 0) {
            // Assign unique matchIndex to each match within the section
            matches.forEach((match, idx) => {
              results.push({
                sectionIndex: i,
                pageNumber: i + 1,
                matchIndex: idx, // Now unique within section: 0, 1, 2, ...
                context: match.excerpt || searchQuery,
                query: searchQuery,
                cfi: match.cfi || null,
              });
            });
          }

          section.unload?.();
        } catch (error) {
          console.error(`Error searching EPUB section ${i}:`, error);
        }
      }
    }
  } catch (error) {
    console.error("Error in EPUB search:", error);
  }

  // If there are no results and the *original* query contained punctuation,
  // split it on those characters and search each piece.  This handles cases
  // where the document retains the punctuation that we stripped above.
  if (results.length === 0 && /[.,;:!?]/.test(rawQuery)) {
    // split the original query around punctuation boundaries and search each
    // piece individually (after normalising it the same way we normalise the
    // main query).
    const rawSegments = rawQuery
      .split(/[.,;:!?]+/)
      .map((s) => s.trim())
      .filter(Boolean);
    for (const rawSeg of rawSegments) {
      if (!rawSeg) continue;
      // normalise this segment exactly the same way as the main query
      const seg = normalizeWhitespace(rawSeg)
        .replace(/\u00A0/g, " ")
        .replace(/[.,;:!?]/g, "");
      if (!seg) continue;
      const segResults = await searchEpub(
        epubBook,
        rendition,
        seg,
        options,
        false,
      );
      results.push(...segResults);
    }
  }

  return results;
};
